import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { PageDirection, PaginationMetadata } from '@/shared/components/table/table.model';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';

export type PaginatorType = 'default' | 'compact';

const DEFAULT_PAGINATION = ({
  previousCursors: [],
  currentCursor: null,
  nextCursor: null,
  pageSize: 0,
  count: 0,
});

@Component({
  selector: 'cosmos-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})

export class PaginatorComponent implements OnInit, OnDestroy {
  @Input() public paginatorType: PaginatorType = 'default';
  public currentPaginationRange$: Observable<string>;
  public fetchedPageCursor$: Observable<string | null | undefined>;
  public currentPageSize$: Observable<number | null>;
  public _currentPageSizeSubject = new BehaviorSubject<number>(20);
  public _previousButtonDisabled = true;
  public _nextButtonDisabled = false;
  public _pageSizeOptions = [20, 100, 500, 1000, 2000];
  public _datasourcePaginationInfoSubject = new BehaviorSubject<PaginationMetadata>(DEFAULT_PAGINATION);
  public paginationData = "";
  private _currentPageNumber = 1;
  private _currentPagination: PaginationMetadata = DEFAULT_PAGINATION;
  private readonly _fetchedPageCursorSubject = new ReplaySubject<string | null | undefined>(1);
  private readonly _currentPaginationRangeSubject = new Subject<string>();

  public ngOnInit(): void {
    this.fetchedPageCursor$ = this._fetchedPageCursorSubject.asObservable();
    this.currentPageSize$ = this._currentPageSizeSubject.asObservable();
    this.currentPaginationRange$ = this._currentPaginationRangeSubject.asObservable();
    this._currentPaginationRangeSubject.next('');
  }

  public ngOnDestroy(): void {
    this._datasourcePaginationInfoSubject.complete();
  }

  public getDatasourceMetaData(paginationInfo: BehaviorSubject<PaginationMetadata>): void {
    paginationInfo.subscribe(pagination => {
      this._currentPagination = pagination;
      this.updatePaginationRange();
    });
    this._currentPageSizeSubject.next(this._currentPagination.pageSize!);
  }

  public onPageChange(requestedChange: PageDirection): void {
    if (requestedChange === 'next') {
      this.switchPageToNext();
    }
    if (requestedChange === 'previous') {
      this.switchPageToPrevious();
    }
    this.updatePaginationRange();
  }

  public onPageSizeChange(newPageSize: number): void {
    this._currentPageSizeSubject.next(newPageSize);
    this._currentPagination.pageSize = newPageSize;
    this._fetchedPageCursorSubject.next(null);
    this.resetPaginator();
  }

  public updatePaginationRange(): void {
    const paginationRangeStart = (this._currentPagination.pageSize!)*(this._currentPageNumber - 1)+1;
    let paginationRangeEnd = (this._currentPagination.pageSize!)*(this._currentPageNumber);

    if (paginationRangeEnd >= this._currentPagination.count!) {
      paginationRangeEnd = this._currentPagination.count!;
      this._nextButtonDisabled = true;
    } else {
      this._nextButtonDisabled = false;
    }

    if (this._currentPagination.count) {
      this.paginationData = `${paginationRangeStart}-${paginationRangeEnd} of ${this._currentPagination.count}`;
    }
  }

  public resetPaginator(): void {
    this._currentPageNumber = 1;
    this._previousButtonDisabled = true;
    this.updatePaginationRange();
  }

  private switchPageToNext(): void {
    this._currentPagination.previousCursors.push(this._currentPagination.currentCursor);
    this._currentPagination.currentCursor = this._currentPagination.nextCursor;
    this._previousButtonDisabled = false;
    this._fetchedPageCursorSubject.next(this._currentPagination.currentCursor);
    this._currentPageNumber++;
  }

  private switchPageToPrevious(): void {
    this._currentPagination.nextCursor = this._currentPagination.currentCursor;
    this._currentPagination.currentCursor = this._currentPagination.previousCursors.pop();
    if (this._currentPagination.previousCursors.length === 0) {
      this._previousButtonDisabled = true;
    }
    this._fetchedPageCursorSubject.next(this._currentPagination.currentCursor);
    this._currentPageNumber--;
  }
}
